/**
 * FigureModePicker — 「이 그림을 **어떤 눈으로** 옮길까」를 고르는 칸.
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md §9-4 「변환 모드」(2026-08-31)
 *
 * 이 칸이 필요한 자리가 둘이다 —
 *   · 그림 하나를 다시 그릴 때 (FigureReconvertModal)
 *   · 본문의 사진을 선 그림으로 바꿀 때 (ProblemViewTab → AiRunModal)
 * 계약이 「`figspec-convert`·재요청 **공통**」이라 적었으니 둘이 같은 것을 골라야 하는데,
 * 폼을 두 벌 쓰면 서버가 모드를 늘릴 때 한쪽만 낡고 그 사실이 화면으로는 안 보인다.
 *
 * **고를 수 있는 것은 자리마다 다르다.** [사진 따라 그리기]는 문제은행 재요청에만 있다 —
 * 그 규칙과 근거는 figureText.figureModeChoices 한 곳에 있고, 여기서는 받아 그리기만 한다.
 * 눌리면 반드시 실패하는 버튼을 내지 않는다는 이 저장소의 규칙(figureText.figureCapabilities)이다.
 *
 * 라디오인 이유: 셋 중 **하나만** 나가고, 무엇이 골라져 있는지가 눌러 보지 않아도 보여야 한다.
 */
import PropTypes from 'prop-types';
import {
  FIGURE_MODE_TEXT, figureModeChoices, figureModeLegend, figureModeHint, figureModeNote,
} from './figureText';

export default function FigureModePicker({
  where, value, onChange, disabled = false, name = 'figure-convert-mode',
  // 생성 얼굴에서만 뜻이 있다 — 대상과 「사진이 있는가」(올린 이미지 또는 그림 원본 카드를 골랐는가)에 따라 갈린다.
  target = null, withImage = false,
  // 생성 얼굴에 이 자리 그림들의 원본 카드가 서 있는가(2026-09-06 A 안) — 안내가 「올리기」만 길인 것처럼 말하지 않게.
  withFigures = false,
}) {
  const choices = figureModeChoices(where, { target, withImage });
  // 고른 방식이 지금 재료로는 안 될 때(생성에서 사진 따라 그리기 + 사진 없음) 그 자리에서 말한다.
  const note = figureModeNote(where, { target, withImage, value, withFigures });
  // target 은 지금 갈리는 값이 없지만(2026-09-05 §10-2 로 두 대상이 같아졌다) 호출부 배선은 그대로 둔다.
  return (
    <fieldset className="space-y-1.5 rounded-xl border border-gray-200 p-3" disabled={disabled}>
      {/* 생성 자리는 「그릴까요」 — 옮길 원본이 없는 자리에서 「옮기기」는 거짓이다(figureModeLegend). */}
      <legend className="px-1 text-[11px] font-medium text-gray-500">{figureModeLegend(where)}</legend>
      {choices.map((m) => (
        <label key={m} className="flex cursor-pointer items-start gap-2">
          <input
            type="radio"
            name={name}
            value={m}
            checked={value === m}
            onChange={() => onChange(m)}
            disabled={disabled}
            className="mt-0.5 size-3.5 shrink-0 accent-primary"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-gray-800">{FIGURE_MODE_TEXT[m].name}</span>
            <span className="block text-[11px] leading-relaxed text-gray-500">{figureModeHint(m, where)}</span>
          </span>
        </label>
      ))}
      {/* 고른 방식에 재료가 모자랄 때 무엇을 하면 되는지 — 잠긴 [만들기]만 남기지 않는다(figureModeNote). */}
      {note && <p className="pt-0.5 text-[11px] leading-relaxed text-amber-700">{note}</p>}
    </fieldset>
  );
}

FigureModePicker.propTypes = {
  // 어느 자리에서 고르는가 — 낼 수 있는 모드가 여기서 갈린다(figureModeChoices).
  // 'generate' 는 그림 생성 창(계약 §9-2 + 2026-09-05 오너 확인 — 생성도 §9-4 의 mode 를 받는다).
  // 종전의 'convert'([선 그림으로 바꾸기])는 화면에서 사라져 뺐다(2026-09-05 감사 — 호출부 0).
  where: PropTypes.oneOf(['bank-reconvert', 'ocr-reconvert', 'generate']).isRequired,
  value: PropTypes.oneOf(['faithful', 'trace', 'schematic']).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  // 한 화면에 이 칸이 둘 이상 뜰 수 있으면 갈라 준다 — 같은 name 이면 라디오가 서로를 끈다.
  name: PropTypes.string,
  target: PropTypes.oneOf(['ocr', 'bank']),
  withImage: PropTypes.bool,
  withFigures: PropTypes.bool,
};
